import { DialogController, DialogService } from 'aurelia-dialog';
import { autoinject } from 'aurelia-framework';
import { ValidationController, Validator } from 'aurelia-validation';
import { StandardValidationBuilder } from 'lib/validation';
import { MooringProductionOrderService, MooringService } from 'services/mooring';
import { UpdateMooringProductionOrderDto } from 'models/mooring';
import { AsyncErrorHandler } from 'lib/ui';
import { CustomerService } from 'services/customer-service';
import { SiteService } from 'services/site-service';
import { I18N } from 'aurelia-i18n';
import { ContactService } from 'services/contact-service';
import { connectTo } from 'aurelia-store';
import { ApplicationState } from 'lib/state';
import { Models } from 'models/core';
import { DeleteDialogService } from 'services/delete-dialog-service';
import { ToastService } from 'services';
import { UserService } from 'services/user-service';

@autoinject
@connectTo()
export class ProductionOrderDetailsDialog {
  protected model: UpdateMooringProductionOrderDto = undefined;

  protected productionOrderId?: number;

  private state: ApplicationState;

  protected customers: Models.Customer[];

  constructor(
    private dialogSerivce: DialogService,
    private dialogControl: DialogController,
    private ruleBuilder: StandardValidationBuilder,
    private toastService: ToastService,
    protected customerService: CustomerService,
    protected siteService: SiteService,
    protected validationController: ValidationController,
    protected validator: Validator,
    protected mooringService: MooringService,
    protected mooringProductionOrderService: MooringProductionOrderService,
    private contactService: ContactService,
    private t: I18N,
    private deleteDialogService: DeleteDialogService,
    private userService: UserService
  ) {
    this.model = new UpdateMooringProductionOrderDto();
    this.createValidationRules();
  }

  @AsyncErrorHandler
  protected async getProductionOrder(id: number) {
    const { data: order } = await this.mooringProductionOrderService.getProductionOrder(id);
    this.model.ResponsibleId = order.ResponsibleId;

    this.model.EstimatedProductionTime = order.EstimatedProductionTime;

    this.model.EstimatedWeightGridLines = order.EstimatedWeightGridLines;
    this.model.EstimatedWeightAnchorLines = order.EstimatedWeightAnchorLines;
    this.model.EstimatedWeightBuoys = order.EstimatedWeightBuoys;
    this.model.EstimatedWeightBridles = order.EstimatedWeightBridles;
  }

  protected activate(args?: { id?: number }) {
    if (!args || !args.id) return;
    this.productionOrderId = args.id;

    void this.init();
  }

  @AsyncErrorHandler
  protected async init() {
    await this.getProductionOrder(this.productionOrderId);
    await this.loadRelevantUsers();
  }

  @AsyncErrorHandler
  async onSave() {
    if (!this.productionOrderId || !(await this.validationController.validate()).valid) return;

    await this.mooringProductionOrderService.updateProductionOrder(this.productionOrderId, this.model);
    this.toastService.showSuccess(this.t.tr('general.saved'));
    this.onCancel();
  }

  onCancel() {
    void this.dialogControl.cancel();
  }

  @AsyncErrorHandler
  protected async getContactInfo(id: number) {
    const contact = await this.contactService.get(id);
    return {
      id: contact.Id,
      title: contact.Name,
    };
  }

  protected confirmBeforeDeleteMooringOffer() {
    void this.deleteDialogService.confirmBeforeDelete(
      () => void this.deleteMooringProductionOrder(),

      'dialog.deleteMooringOfferHeading',
      'dialog.deleteOfferAreYouSure',
      'dialog.deleteOffer'
    );
  }

  private async deleteMooringProductionOrder() {
    // await this.mooringOfferService.deleteOffer(this.offerId);
    // this.dialogControl.cancel();
    // this.toastService.showSuccess('mooring.mooringOfferDeleted');
    // this.router.navigateToRoute('mooring-offers-view');
  }

  private createValidationRules() {
    this.ruleBuilder
      .with(this.model)
      .min('EstimatedProductionTime', 0)
      .min('EstimatedWeightGridLines', 0)
      .min('EstimatedWeightAnchorLines', 0)
      .min('EstimatedWeightBuoys', 0)
      .min('EstimatedWeightBridles', 0)
      .min('EstimatedWeightFleetAnchorLines', 0)
      .required('ResponsibleId')
      .done();
  }

  protected responsibles: { Id: string; Name: string }[] = [];

  protected async getResponsibleInfo(id: number) {
    if (!id) {
      return null;
    }
    const responsible = await this.userService.get(id);

    return {
      id: responsible.Id,
      title: responsible.Name,
    };
  }

  protected findResponsible(searchText: string) {
    return this.responsibles
      .filter((c) => c.Name.toLowerCase().includes(searchText.toLowerCase()))
      .map((c) => ({
        id: c.Id,
        title: c.Name,
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }

  protected responsibleSelected(responsible: { id: string }) {
    this.model.ResponsibleId = responsible.id;
  }

  @AsyncErrorHandler
  async loadRelevantUsers() {
    const users = await this.userService.getAll(`?&filter=Deleted eq null`);

    this.responsibles = users
      .map((u) => ({
        Id: u.Id,
        Name: u.Name,
      }))
      .sort((a, b) => a.Name.localeCompare(b.Name));
  }
}
